import { Box } from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import ButtonGroup from "../Shared/ButtonGroup";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setVBCCount } from "../../store/providerReducer";
import { VBCAlertRow } from "../../types/vbc-alerts";
import {
  buildVBCAlertRows,
  countVisibleVBCAlertRows,
  extractVBCAlertsV2,
} from "../../utils/vbc-alert-utils";
import VBCAlertsV2Table from "./VBCAlertsV2Table";
import PollingRefreshIndicator from "../Shared/PollingRefreshIndicator";
import {
  useUpdateVBCAlertStatus,
  useVBCAlerts,
} from "../../hooks/queries";

const VBC = () => {
  const [activeStatusFilter, setActiveStatusFilter] =
    useState<string>("All");
  const [activeFilter, setActiveFilter] = useState<string>("All Alerts");
  const mrn = useAppSelector((state) => state?.fhir?.mrn);
  const dispatch = useAppDispatch();

  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, VBCAlertRow["status"]>
  >({});

  const {
    data: response,
    isLoading: initialLoading,
    isFetching,
    error,
  } = useVBCAlerts(mrn ?? undefined, { polling: true });

  const updateVBCAlert = useUpdateVBCAlertStatus();

  const alertData = useMemo(() => {
    let rows: VBCAlertRow[] = [];
    if (response?.status === 200) {
      rows = buildVBCAlertRows(extractVBCAlertsV2(response.data));
    } else if ((error as any)?.response?.status === 404) {
      rows = [];
    }

    if (Object.keys(statusOverrides).length === 0) {
      return rows;
    }

    return rows.map((alert) =>
      statusOverrides[alert.id]
        ? { ...alert, status: statusOverrides[alert.id] }
        : alert
    );
  }, [response, error, statusOverrides]);

  useEffect(() => {
    if (response?.status === 200) {
      dispatch(setVBCCount(countVisibleVBCAlertRows(alertData)));
    } else if ((error as any)?.response?.status === 404) {
      dispatch(setVBCCount(0));
    }
  }, [alertData, dispatch, error, response?.status]);

  // Drop overrides once server data already reflects acknowledged status
  useEffect(() => {
    if (response?.status !== 200) {
      return;
    }

    const serverRows = buildVBCAlertRows(extractVBCAlertsV2(response.data));
    setStatusOverrides((current) => {
      const entries = Object.entries(current);
      if (entries.length === 0) {
        return current;
      }

      const next: Record<string, VBCAlertRow["status"]> = {};
      let changed = false;

      entries.forEach(([id, status]) => {
        const serverRow = serverRows.find((row) => row.id === id);
        if (!serverRow || serverRow.status !== status) {
          next[id] = status;
        } else {
          changed = true;
        }
      });

      return changed || Object.keys(next).length !== entries.length
        ? next
        : current;
    });
  }, [response]);

  const isRefreshing = isFetching && !initialLoading;

  async function handleTabChange(tab: string) {
    setActiveFilter(tab);
  }

  async function handleStatusFilterChange(tab: string) {
    setActiveStatusFilter(tab);
  }

  const filteredAlerts = useMemo(() => {
    let nextAlerts = alertData;

    if (activeStatusFilter !== "All") {
      const expectedStatus = activeStatusFilter.toUpperCase();
      nextAlerts = nextAlerts.filter((alert) => alert.status === expectedStatus);
    }

    if (activeFilter === "Quality Measures") {
      return nextAlerts.filter((alert) => alert.kind === "QUERY");
    }

    if (activeFilter === "Suspected HCC Diagnosis") {
      return nextAlerts.filter((alert) => alert.kind === "SUSPECT_DIAGNOSIS");
    }

    if (activeFilter === "Recommended Specialist Visits") {
      return nextAlerts.filter((alert) => alert.kind === "SPECIALIST_VISIT");
    }

    if (activeFilter === "Hospitalizations") {
      return nextAlerts.filter((alert) => alert.kind === "HOSPITALIZATION");
    }

    // All Alerts: no type filter
    return nextAlerts;
  }, [activeFilter, activeStatusFilter, alertData]);

  const handleAcknowledge = useCallback(
    async (alertsToAcknowledge: VBCAlertRow[]) => {
      // Prefer Arc+ `alertId`; fall back to row `id` when alertId is null/missing.
      const actionableAlerts = alertsToAcknowledge
        .map((alert) => ({
          alert,
          resolvedAlertId: alert.alertId || alert.id,
        }))
        .filter(({ resolvedAlertId }) => Boolean(resolvedAlertId) && Boolean(mrn));

      if (actionableAlerts.length === 0) {
        console.log(
          "Missing alert id or MRN for VBC acknowledge.",
          alertsToAcknowledge.map((a) => ({ id: a.id, alertId: a.alertId }))
        );
        return false;
      }

      try {
        await Promise.all(
          actionableAlerts.map(({ alert, resolvedAlertId }) =>
            updateVBCAlert.mutateAsync({
              alertId: resolvedAlertId,
              mrnNo: mrn,
              status: "ACKNOWLEDGED",
              serviceYear: "2025",
            })
          )
        );

        setStatusOverrides((current) => {
          const next = { ...current };
          actionableAlerts.forEach(({ alert }) => {
            next[alert.id] = "ACKNOWLEDGED";
          });
          return next;
        });

        return true;
      } catch (ackError) {
        console.log("Error acknowledging VBC alert");
        return false;
      }
    },
    [mrn, updateVBCAlert]
  );

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        px={"md"}
        py={"xs"}
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          columnGap: "12px",
          flexShrink: 0,
          minWidth: 0,
          width: "100%",
          overflowX: "auto",
        }}
      >
        <ButtonGroup
          buttons={[
            "All Alerts",
            "Suspected HCC Diagnosis",
            "Quality Measures",
            "Hospitalizations",
            "Recommended Specialist Visits",
          ]}
          defaultActive='All Alerts'
          onChange={handleTabChange}
          leadingIcons={{
            "Suspected HCC Diagnosis": (
              <Box
                component='img'
                src='/ai-bulwark-logo.svg'
                alt=''
                aria-hidden
                h={20}
                w='auto'
                style={{
                  flexShrink: 0,
                  objectFit: "contain",
                  display: "block",
                  marginTop: 2,
                }}
              />
            ),
          }}
        />
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexShrink: 0,
          }}
        >
          <PollingRefreshIndicator visible={isRefreshing} />
          <ButtonGroup
            buttons={["All", "Pending", "Acknowledged"]}
            defaultActive='All'
            onChange={handleStatusFilterChange}
          />
        </Box>
      </Box>
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <VBCAlertsV2Table
          alerts={filteredAlerts}
          loading={initialLoading}
          onAcknowledge={handleAcknowledge}
        />
      </Box>
    </div>
  );
};

export default VBC;
